import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const Ctx = createContext(null);
const SYSTEM_PATHS = ["/admin/settings", "/admin/users", "/admin/billing", "/admin/layout", "/admin/labels", "/admin/nds", "/admin/db", "/admin/roi", "/admin/tutorials", "/admin/sms"];
const KIND_LABEL = { campus_residents: "resident file", fin_reports: "finance report", re_session: "sit-down session", ops_problems: "problems inbox", fin_ledger: "resident ledger",
  grant_report: "grant report", grant_tile: "grants tile", partner_tile: "partners tile" };

const clean = (t) => String(t || "").replace(/^\/\/\s*/, "").trim();

/** One shared "what's on screen" for Ally OS: open windows register here; the top (focused) one wins, else the page. */
export function AllyContextProvider({ children }) {
  const [wins, setWins] = useState([]);
  const [details, setDetails] = useState({});
  const setDetail = useCallback((id, d) => setDetails((x) => ({ ...x, [id]: d })), []);
  const clearDetail = useCallback((id) => setDetails(({ [id]: _, ...rest }) => rest), []);
  const value = useMemo(() => ({ wins, setWins, details, setDetail, clearDetail }), [wins, details, setDetail, clearDetail]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Window layers publish their open (not minimized) windows: [{id, kind, title, z, payload}]. */
export function usePublishWindows(list) {
  const c = useContext(Ctx);
  const sig = JSON.stringify(list.map((w) => [w.id, w.kind, w.title, w.z]));
  useEffect(() => { c?.setWins(list); }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => c?.setWins([]), []); // eslint-disable-line react-hooks/exhaustive-deps
}

/** A window adds its current card/step (and resident) to the context while it's open. */
export function useAllyDetail(winId, detail) {
  const c = useContext(Ctx);
  const sig = JSON.stringify(detail);
  useEffect(() => { if (winId) c?.setDetail(winId, detail); }, [winId, sig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (winId) c?.clearDetail(winId); }, [winId]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useAllyCurrent(path) {
  const c = useContext(Ctx);
  return useMemo(() => {
    const open = c?.wins || [];
    const top = open.length ? open.reduce((a, b) => (b.z > a.z ? b : a)) : null;
    const others = open.filter((w) => w !== top).map((w) => clean(w.title)).slice(0, 6).join(" | ");
    if (top) {
      const p = top.payload || {};
      const rid = p.residentId || p.resident_id || p.focus?.residentId;
      const title = clean(top.title);
      return { path, kind: KIND_LABEL[top.kind] || top.kind, title, step: c.details[top.id]?.step,
        ...(rid ? { resident_id: rid, resident_name: p.name || p.resident_name || title.split(" · ").pop() } : {}), open_windows: others || undefined };
    }
    if (SYSTEM_PATHS.some((s) => path.startsWith(s))) return { path, kind: "system", title: `System · ${path.split("/")[2].replace(/^\w/, (x) => x.toUpperCase())}` };
    if (path === "/admin" || path === "/os") return { path, kind: "desk", title: "Main desk" };
    if (path.startsWith("/admin/w/")) return { path, kind: KIND_LABEL[path.split("/")[3]] || path.split("/")[3], title: clean(document.title.replace(" · TEA", "")) };
    return { path, kind: "page", title: path.split("/").filter(Boolean).slice(1).join(" · ") || path };
  }, [c, path]);
}
